import React, { useState, useRef, useEffect } from 'react'
import { ICONS } from '@/constants/constants'
import Image from 'next/image'

interface SearchTripDropDownProps {
    tripType?: 'roundtrip' | 'oneway';
    label: string;
    value: string;
    placeholder?: string;
    icon?: string;
    arrow?:boolean;
    options?: string[];
    onSelect?: (value: string) => void;
    onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
    className?: string;
    showDropdownArrow?: boolean;
    isDropdownOpen?: boolean; // External control for dropdown state
}

const SearchTripDropDown = ({
    label,
    value,
    placeholder,
    icon,
    arrow=false,
    tripType,
    options = [],
    onSelect,
    onClick,
    className = "",
    showDropdownArrow = true,
    isDropdownOpen: externalIsOpen
}: SearchTripDropDownProps) => {
    const [internalIsOpen, setInternalIsOpen] = useState(false)

    // Use external isOpen if provided, otherwise use internal state
    const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen
    const dropdownRef = useRef<HTMLDivElement>(null)

    // Close dropdown when clicking outside (only for internal dropdown)
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                if (externalIsOpen === undefined) {
                    setInternalIsOpen(false)
                }
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [externalIsOpen])

    const handleSelect = (option: string) => {
        onSelect?.(option)
        setInternalIsOpen(false)
    }

    return (
        <div className={`flex flex-col relative ${className}`} ref={dropdownRef}>
            <div className='flex justify-between'>
                <label className="text-sm text-gray-600 mb-1 font-medium h-5">
                    {label}
                </label>
{
    arrow ? (
        tripType === 'roundtrip' ? (
            <Image src={ICONS.arrows} alt='swap' width={16} height={16} className='rotate-90' />
        ) : (
            <Image src={ICONS.singlearrow} alt='swap' width={16} height={16} style={{ transform: 'rotate(90deg)' }} />
        )
    ) : null
}
            </div>

            <div
                className="flex items-center gap-2 p-3 border border-gray-300 rounded-md bg-white cursor-pointer hover:border-gray-400 transition-colors h-[35px]"
                onClick={(event) => {
                    if (showDropdownArrow && externalIsOpen === undefined) {
                        setInternalIsOpen(!internalIsOpen)
                    }
                    if (onClick) {
                        onClick(event)
                    }
                }}
            >
                {icon && (
                    <Image
                        src={icon}
                        alt={label}
                        width={20}
                        height={20}
                        className="text-gray-500 flex-shrink-0"
                    />
                )}
                <span className={`flex-1 text-sm ${!value ? 'text-gray-400' : 'text-gray-900'}`}>
                    {value || placeholder}
                </span>
                {showDropdownArrow && (
                    <Image
                        src={ICONS.downarrow}
                        alt="dropdown"
                        width={16}
                        height={16}
                        className={`text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    />
                )}
            </div>

            {/* Dropdown Menu */}
            {isOpen && showDropdownArrow && options.length > 0 && externalIsOpen === undefined && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
                    {options.map((option, index) => (
                        <div
                            key={index}
                            className={`p-3 cursor-pointer hover:bg-gray-50 transition-colors text-sm ${option === value ? 'bg-blue-50 text-blue-600' : 'text-gray-900'
                                }`}
                            onClick={() => handleSelect(option)}
                        >
                            {option}
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

export default SearchTripDropDown